import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  UserCog,
  Menu,
  X,
  LogOut,
} from 'lucide-react';

/**
 * Sidebar con navigazione role-based
 */
function Sidebar() {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Chiudi sidebar su cambio route (mobile)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Previeni scroll del body quando sidebar è aperta
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Navigazione in base al ruolo
  const getNavItems = () => {
    const items = [];

    // Menu Admin
    if (user?.role === 'admin') {
      items.push(
        {
          label: 'Dashboard',
          href: '/dashboard',
          icon: LayoutDashboard,
          roles: ['admin'],
        },
        {
          label: 'Utenti',
          href: '/users',
          icon: Users,
          roles: ['admin'],
        },
        {
          label: 'Tutti i documenti',
          href: '/documents',
          icon: FileText,
          roles: ['admin'],
        },
        {
          label: 'Assegnazioni',
          href: '/assignments',
          icon: UserCog,
          roles: ['admin'],
        }
      );
    }

    // Menu Operatore
    if (user?.role === 'operator') {
      items.push(
        {
          label: 'I miei clienti',
          href: '/my-clients',
          icon: Users,
          roles: ['operator'],
        },
        {
          label: 'Documenti',
          href: '/documents',
          icon: FileText,
          roles: ['operator'],
        }
      );
    }

    // Menu Cliente
    if (user?.role === 'client') {
      items.push({
        label: 'I miei documenti',
        href: '/my-documents',
        icon: FileText,
        roles: ['client'],
      });
    }

    // Impostazioni per tutti
    items.push({
      label: 'Impostazioni',
      href: '/settings',
      icon: Settings,
      roles: ['admin', 'operator', 'client'],
    });

    return items.filter((item) => item.roles.includes(user?.role));
  };

  const navItems = getNavItems();

  const navLinkClasses = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3.5 sm:py-3 rounded-lg transition-colors min-h-[48px] sm:min-h-[44px] active:scale-[0.98] ${
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground active:bg-accent'
    }`;

  return (
    <>
      {/* Bottone mobile per aprire sidebar */}
      <button
        className="fixed top-3 left-3 z-50 md:hidden p-2.5 rounded-lg bg-card border border-border shadow-md min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95 transition-transform"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Chiudi menu' : 'Apri menu'}
        style={{
          top: 'max(0.75rem, env(safe-area-inset-top))',
          left: 'max(0.75rem, env(safe-area-inset-left))',
        }}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
          style={{ touchAction: 'none' }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full h-[100dvh] w-[280px] sm:w-64 bg-card border-r border-border
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          overflow-y-auto overscroll-contain
        `}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 sm:px-6 border-b border-border sticky top-0 bg-card z-10">
          <h1 className="text-lg sm:text-xl font-bold text-primary">Portale Famiglia</h1>
        </div>

        {/* Navigazione */}
        <nav className="p-3 sm:p-4 space-y-1.5 sm:space-y-2 pb-24">
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={navLinkClasses}
              onClick={() => setIsOpen(false)}
            >
              <item.icon size={22} className="flex-shrink-0" />
              <span className="text-base sm:text-sm">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Info utente in basso */}
        <div 
          className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 border-t border-border bg-card"
          style={{
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-medium text-base sm:text-sm">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
            <button
              onClick={logout}
              className="p-2.5 sm:p-2 rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Logout"
            >
              <LogOut size={22} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
